/**
 * useContent - owner-editable site content (contract v1.2 §3.11)
 *
 * Renders the bundled seed copy at once, fetches
 * GET /api/public/content/<collection> once per page load, and swaps in the
 * API items when they arrive. If the call fails or takes longer than
 * CONTENT_TIMEOUT_MS the bundled copy stays, so an API outage never blanks
 * a page.
 *
 * Every component reading the same collection shares one request and its
 * result (module-level cache). Unmounting stops listening; the request is
 * aborted only when nobody is waiting for it any more.
 *
 * @example
 * const { items } = useContent('groups');
 * const { banner, social, notes } = useSiteSettings();
 */

import { useEffect, useMemo, useState } from 'react';
import { getContent } from '../services/api/contentService';
import { bundledItems, siteSettings } from '../data/content';

/** How long the bundled copy waits for the API before it stays for good. */
export const CONTENT_TIMEOUT_MS = 4000;

/**
 * collection -> { promise, result, controller, users, cancelled }.
 * `result` is null while pending, then {source: 'api', items} or
 * {source: 'bundled'}.
 */
const requests = new Map();

function startRequest(collection) {
  const controller = new AbortController();
  const entry = { result: null, controller, users: 0, cancelled: false };
  let timer;
  const tooSlow = new Promise((resolve) => {
    timer = setTimeout(() => {
      controller.abort();
      resolve({ source: 'bundled' });
    }, CONTENT_TIMEOUT_MS);
  });
  const answer = getContent(collection, { signal: controller.signal }).then(
    (data) => ({ source: 'api', items: data.items }),
    () => ({ source: 'bundled' })
  );
  entry.promise = Promise.race([answer, tooSlow])
    .then((result) => {
      clearTimeout(timer);
      if (entry.cancelled) {
        // Everyone left before it answered: let the next reader fetch again.
        if (requests.get(collection) === entry) requests.delete(collection);
      } else {
        entry.result = result;
      }
      return result;
    });
  requests.set(collection, entry);
  return entry;
}

const settledResult = (collection) => {
  const entry = requests.get(collection);
  return entry ? entry.result : null;
};

/**
 * @param {string} collection - One of CONTENT_COLLECTIONS
 * @returns {{items: Array<{slug: string, position: number, data: object}>, source: 'api'|'bundled', loading: boolean}}
 */
const useContent = (collection) => {
  const [result, setResult] = useState(() => settledResult(collection));

  useEffect(() => {
    const entry = requests.get(collection) || startRequest(collection);
    if (entry.result) {
      setResult(entry.result);
      return undefined;
    }

    let active = true;
    entry.users += 1;
    entry.promise.then((settled) => {
      if (active) setResult(settled);
    });

    return () => {
      active = false;
      entry.users -= 1;
      // Deferred, so an immediate remount (StrictMode, a quick route change)
      // keeps the same request instead of starting a second one.
      setTimeout(() => {
        if (entry.users === 0 && !entry.result && !entry.cancelled) {
          entry.cancelled = true;
          entry.controller.abort();
        }
      }, 0);
    };
  }, [collection]);

  const apiItems = result && result.source === 'api' ? result.items : null;
  const items = useMemo(() => apiItems || bundledItems(collection), [apiItems, collection]);

  return {
    items,
    source: apiItems ? 'api' : 'bundled',
    loading: !result,
  };
};

/**
 * The `settings` collection as {banner, social, notes} (see data/content.js
 * siteSettings). One shared request with every other settings reader.
 */
export const useSiteSettings = () => {
  const { items } = useContent('settings');
  return useMemo(() => siteSettings(items), [items]);
};

/** Forget every cached or pending content request (tests). */
export const resetContentCache = () => {
  requests.forEach((entry) => {
    if (!entry.result) {
      entry.cancelled = true;
      entry.controller.abort();
    }
  });
  requests.clear();
};

export default useContent;
